import * as R from "ramda";
import moment from "moment";
import { Card, Col, Empty, Row, Spin, Tabs } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  showMediaOssUrl,
  previewMediaOssBaseUrlWithCdn,
} from "./upload-resource";
import {
  EditDesc,
  EditName,
  EditCover,
  getDetail,
  VisibleSwitch,
} from "./base-type-carousel-item";
import { AsrLine } from "./asr-line";
import { TagListContent } from "./list-item";
import { getAiResult } from "../aliyun-service-api";
import { VideoWithSlider } from "./video-with-slider";
import {
  channelToText,
  uploadResourceChannelMap,
} from "@/containers/Video/tab-pages/video-resource-page";
import { useUserName } from "@/components/Order/FindUserHOC";
import { useTypeName } from "@/containers/Video/widgets/upload-resource-modal";
import { sizeToHumanReadableSize } from "@/containers/Video/widgets/upload-resource-util";

const { TabPane } = Tabs;

export const YYYYMMDDHHmmss = "YYYY-MM-DD HH:mm:ss";

export const YYYYMMDDHHmmssFunc = (v) => moment(v).format(YYYYMMDDHHmmss);

export const DetailInfo = ({ label, value, hidden }) => {
  if (hidden) {
    return null;
  }
  return (
    <Row gutter={16}>
      <Col span={8}>{label}</Col>
      <Col span={16}>{value}</Col>
    </Row>
  );
};

export const ShowUserNameByUserId = ({ userId }) => {
  const userName = useUserName(userId);
  return <span>{userName}</span>;
};

export const useRawData = (props) => {
  const detail = R.pathOr({}, ["detail"], props);
  const activeItem = R.pathOr({}, ["activeItem"], props);
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState({});
  const shouldRequest = R.equals(activeItem, detail);

  useEffect(() => {
    if (!shouldRequest) {
      return;
    }
    const id = R.pathOr("", ["detail", "id"], props);
    if (!id) {
      return;
    }

    setLoading(true);
    getDetail(detail)
      .then((res) => {
        setRawData(res);
      })
      .finally(() => setLoading(false));
  }, [shouldRequest]);

  return {
    rawData,
    loading,
    shouldRequest,
  };
};

export const BaseInfoCard = (props) => {
  const { rawData = {}, detail = {} } = props;
  const { type } = detail;

  const showTypeName = useTypeName({ type });

  return (
    <Card>
      <DetailInfo
        label={`${showTypeName}名称`}
        value={<EditName {...props} />}
      />
      <DetailInfo
        hidden={type !== "video"}
        label={`${showTypeName}封面`}
        value={<EditCover {...props} />}
      />
      <DetailInfo label={"公开状态"} value={<VisibleSwitch {...props} />} />
      <DetailInfo
        label={`${showTypeName}大小`}
        value={R.pipe(
          R.pathOr("", [
            "aliyuninfo",
            "FileInfoList",
            0,
            "FileBasicInfo",
            "FileSize",
          ]),
          Number,
          sizeToHumanReadableSize
        )(rawData)}
      />
      <DetailInfo
        hidden={type !== "video"}
        label={"视频尺寸"}
        value={
          R.pathOr(
            "",
            ["aliyuninfo", "FileInfoList", 0, "FileBasicInfo", "Width"],
            rawData
          ) +
          " * " +
          R.pathOr(
            "",
            ["aliyuninfo", "FileInfoList", 0, "FileBasicInfo", "Height"],
            rawData
          )
        }
      />
      <DetailInfo
        label={"上传者"}
        value={
          <ShowUserNameByUserId
            userId={R.pathOr("", ["item", "auth_by"], rawData)}
          />
        }
      />
      <DetailInfo
        label="分类"
        value={R.pipe(
          R.pathOr("", ["item", "channel"]),
          channelToText
        )(rawData)}
      />
      <DetailInfo label="标签" value={<TagListContent {...props} />} />
      <DetailInfo
        label="创建时间"
        value={R.pipe(
          R.pathOr("", ["item", "CreatedAt"]),
          YYYYMMDDHHmmssFunc
        )(rawData)}
      />
      <DetailInfo
        label="更新时间"
        value={R.pipe(
          R.pathOr("", ["item", "UpdatedAt"]),
          YYYYMMDDHHmmssFunc
        )(rawData)}
      />
      <DetailInfo label="说明" value={<EditDesc {...props} />} />
    </Card>
  );
};

export const replaceMiddleDoubleSlash = (url) => {
  /**
   * ([^:])  匹配除了冒号以外的任意字符, 非零宽断言, 含义为
   *         匹配一个位置, 这个位置的前面不能是冒号
   *
   * 那么下面的:
   * ([^:]//) 匹配一个位置, 这个位置的前面不能是冒号,且后面是两个斜杠,并把这个位置的前面的字符作为第一个分组
   *
   * 例如: //jfoto-media-resources.oss-cn-beijing.aliyuncs.com//icetemp/1150/20241202112039.mp4
   * 那么匹配到的分组就是  m//, 那么组内就是 m, 将其替换为 m/
   */
  return url.replace(/([^:])\/\//g, "$1/");
};

export const convertPathToUrl = (path) => {
  if (path.startsWith("//")) {
    return path;
  }
  return R.pipe(
    //  拼接 mediaOssBaseUrl
    (path) => previewMediaOssBaseUrlWithCdn + path,
    //  替换中间的双斜杠
    replaceMiddleDoubleSlash
  )(path);
};

export const BaseTypeCarouselItemVideo = (props) => {
  const detail = R.pathOr({}, ["detail"], props);
  const gold_slogan = R.pathOr([], ["gold_slogan"], detail);

  const { rawData, loading, shouldRequest } = useRawData(props);

  const videoUrl = (() => {
    const path = R.pathOr("", ["item", "path"], rawData);
    if (path.startsWith("//")) {
      return path;
    }
    return R.pipe(
      //  取出 item.path
      R.pathOr("", ["item", "path"]),
      convertPathToUrl
    )(rawData);
  })();

  const ref = useRef(null);
  const [asrList, setAsrList] = useState([]);
  const [requestOssUrlKey, setRequestOssUrlKey] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);

  const asrListWithGoldSlogan = useMemo(() => {
    return asrList.map((item) => {
      const isGolden = R.includes(item, gold_slogan);
      return {
        ...item,
        golden: isGolden,
      };
    });
  }, [asrList, gold_slogan]);

  useEffect(() => {
    if (!shouldRequest && ref.current) {
      if (!ref.current.paused) {
        ref.current.pause();
      }
    }
  }, [shouldRequest, ref]);

  useEffect(() => {
    if (!shouldRequest) {
      return;
    }
    const result = R.pathOr("", ["asr_oss_url"], detail);
    if (result) {
      setAiLoading(true);
      const fullUrl = showMediaOssUrl + result;
      const currentProtocol = window.location.protocol;
      const url = `${currentProtocol}${fullUrl}`;
      getAiResult(url)
        .then((list) => setAsrList(R.map(R.omit(["golden"]))(list)))
        .finally(() => {
          setAiLoading(false);
        });
    }
  }, [detail, requestOssUrlKey, shouldRequest]);

  if (loading) {
    return <Spin />;
  }

  return (
    <Row
      gutter={16}
      style={{
        maxHeight: "calc(100% - 40px)",
        padding: 20,
      }}
    >
      <Col
        span={12}
        className="flex-x-center ant-card-bordered p-6-i"
        style={{
          maxWidth: "100%",
        }}
      >
        <VideoWithSlider
          key={shouldRequest}
          active={shouldRequest}
          ref={ref}
          className="img-fit"
          style={{
            maxHeight: "80vh",
          }}
          src={videoUrl}
          controls
        />
      </Col>
      <Col span={12}>
        <Tabs defaultActiveKey="1" type="card" className="bg-white h-full">
          <TabPane tab="详情" key="1">
            <BaseInfoCard rawData={rawData} {...props} />
          </TabPane>
          <TabPane tab="语音识别" key="2">
            <div>
              <Spin spinning={aiLoading}>
                {asrListWithGoldSlogan.length > 0 ? (
                  <AsrLine
                    goldSlogan={gold_slogan}
                    reloadAsr={() => setRequestOssUrlKey(R.inc)}
                    target={ref}
                    asrList={asrListWithGoldSlogan}
                    showTime
                    detail={rawData}
                    orderId={R.pathOr("", ["item", "order_id"], rawData)}
                    userId={R.pathOr("", ["user", "id"], props)}
                  />
                ) : (
                  <Empty
                    description={
                      R.pathEq(
                        uploadResourceChannelMap.智能剪辑视频,
                        ["item", "channel"],
                        rawData
                      )
                        ? "智能剪辑视频无语音识别功能"
                        : "暂无语音识别"
                    }
                  />
                )}
              </Spin>
            </div>
          </TabPane>
        </Tabs>
      </Col>
    </Row>
  );
};



// WEBPACK FOOTER //
// ./src/containers/Video/widgets/base-type-carousel-item-video.js